import type Map from '@/components/map/Map.vue'
import { ItemType } from '@/models/enums/app/ItemType'
import { StoresList } from '@/models/enums/app/StoresList'
import { LayerType } from '@/models/enums/geo/LayerType'
import type { GeoData } from '@/models/interfaces/geo/GeoData'
import type { CommuneViewData } from '@/services/map/CommuneViewBoundsService'
import type { Item } from '@/models/interfaces/Item'
import type { AtlasActive, AtlasMap } from '@/models/interfaces/map/AtlasMap'
import type { AtlasCategoryActive } from '@/models/interfaces/map/AtlasCategoryActive'
import { AtlasCategoriesService } from '@/services/map/osm/AtlasCategoriesService'
import type { Layer } from '@/models/interfaces/map/Layer'
import type { UserDroppedLayer } from '@/models/interfaces/map/UserDroppedLayer'
import type { PointAnnotation } from '@/composables/useMapDraw'
import type {
  ActiveLayerEntry,
  UnifiedMapLegendEntry,
} from '@/models/interfaces/map/ActiveLayerLegend'
import type { AppLayerLegendItem, AtlasLayerLegendItem } from '@/models/interfaces/map/Legend'
import type {
  FondLayerStyle,
  FondLayerVisibleIn,
  MapStylesListEntry,
} from '@/models/interfaces/map/FondLayerStyle'
import type { MapState } from '@/models/interfaces/map/MapState'
import type { CommuneData } from '@/views/map/components/CommuneCard.vue'
import {
  CommuneService,
  getCommuneGeometryFromApi,
  mapApiResponseToCommuneData,
  type CommuneApiResponse,
} from '@/services/commune/CommuneService'
import { AppLayersService } from '@/services/map/AppLayersService'
import { CommuneLayerService } from '@/services/map/CommuneLayerService'
import { getCommuneBoundsFromMap } from '@/services/map/CommuneViewBoundsService'
import { getCommuneViewDataFromGeoJSON } from '@/data/communesCameroonGeoJSON'
import { getMapStylesListSplit } from '@/services/map/MapStylesService'
import { AtlasMapService } from '@/services/map/AtlasMapService'
import { FondLayerService } from '@/services/map/FondLayerService'
import { LegendService } from '@/services/map/LegendService'
import MapService from '@/services/map/MapService'
import { MapStoreSerializationService } from '@/services/map/MapStoreSerializationService'
import type { Project } from '@/models/interfaces/Project'
import { ProjectService } from '@/services/projects/ProjectService'
import { ResourceService } from '@/services/resources/ResourceService'
import type { Resource } from '@/models/interfaces/Resource'
import {
  createEmptyProjectMapFilters,
  type ProjectMapFilters,
} from '@/models/interfaces/map/ProjectMapFilters'
import {
  createEmptyResourceMapFilters,
  type ResourceMapFilters,
} from '@/models/interfaces/map/ResourceMapFilters'
import type { UseMapDrawReturn } from '@/composables/useMapDraw'
import type { LngLat, LngLatBounds } from 'maplibre-gl'
import { defineStore } from 'pinia'
import { computed, reactive, ref, shallowRef, watch, type Ref } from 'vue'
import { useApplicationStore } from './applicationStore'
// TODO: Réactiver lorsque l'endpoint /api/atlases sera disponible
// import { useAtlasStore } from './atlasStore'

export const useMyMapStore = defineStore(StoresList.MY_MAP, () => {
  const applicationStore = useApplicationStore()
  const myMap: Ref<InstanceType<typeof Map> | undefined> = ref()
  const map = computed(() => myMap.value?.map)
  const mapCanvasToDataUrl: Ref<string | null> = ref(null)
  const isRightSidebarShown = ref(true)
  const isLeftSidebarShown = ref(true)
  /** G1: when true, the right sidebar content panel (subcategories) is unfolded; arrow button toggles this. */
  const rightSidebarPanelUnfolded = ref(false)
  /** G2: which right sidebar panel content is shown: OSM categories or Annotate map tools. */
  const rightSidebarPanelVariant = ref<'osm' | 'annotate'>('osm')
  /** Draw API from useMapDraw (set by MyMap when map is ready). */
  const mapDrawApi: Ref<UseMapDrawReturn | null> = shallowRef(null)

  function setMapDrawApi(api: UseMapDrawReturn | null): void {
    mapDrawApi.value = api
  }
  const mapSearch: Ref<GeoData | null> = ref(null)
  /** When set, commune selection is locked to this name (communal geoportal mode). */
  const lockedCommuneName: Ref<string | null> = ref(null)
  /**
   * cmnCode resolved for the locked commune, set as soon as the commune API result comes back
   * (see applyCommuneViewFromApiGeometry). Deliberately NOT derived from communeViewData: the
   * /api/communes collection/search endpoints used to resolve a commune by name don't return the
   * polygon geometry (only the detail endpoint does), so gating cmnCode on geometry left
   * activeCommuneCode stuck at null — and every cmnCode-scoped call silently falling back to the
   * national scope — whenever the mask geometry didn't load.
   */
  const lockedCommuneCode: Ref<string | null> = ref(null)

  function setLockedCommuneName(name: string | null): void {
    const trimmed = name?.trim() ?? ''
    const next = trimmed ? trimmed : null
    if (next !== lockedCommuneName.value) {
      lockedCommuneCode.value = null
    }
    lockedCommuneName.value = next
  }
  /** Bounds and geometry from clicked commune (layer), with name to match mapSearch. When set and name matches, Map uses it for mask + maxBounds. */
  const communeViewData: Ref<({ name: string; cmnCode?: string } & CommuneViewData) | null> = ref(null)

  /**
   * cmnCode of the commune currently locked in the communal geoportal, or null outside it.
   * Pass to commune-scoped endpoints (GET /api/atlas_categories?cmnCode=..., GET /api/map_styles/
   * infra_type_ouvrage_{typId}?cmnCode=...) so the right panel and its map layers only show
   * infrastructures within this commune's perimeter.
   */
  const activeCommuneCode = computed(() =>
    lockedCommuneName.value != null ? lockedCommuneCode.value : null,
  )
  const isMapAlreadyBeenMounted = ref(false)
  const isLayersReorderingAlreadyTriggering = ref(false)
  const isMapExportActive = ref(false)

  const actorLayer: Ref<Layer | null> = ref(null)
  const actorSubLayers: Ref<Layer[]> = ref([])
  const resourceLayer: Ref<Layer | null> = ref(null)
  const resourceSubLayers: Ref<Layer[]> = ref([])
  /** When set, map GeoJSON uses this list instead of resourceStore.resources (server + client filtered). */
  const resourceMapFilteredList: Ref<Resource[] | null> = ref(null)
  const resourceMapFilters: ResourceMapFilters = reactive(createEmptyResourceMapFilters())

  const projectMapFilteredList: Ref<Project[] | null> = ref(null)
  const projectMapFilters: ProjectMapFilters = reactive(createEmptyProjectMapFilters())

  const isResourceMapFiltersActive = computed(() =>
    Object.values(resourceMapFilters).some((value) => String(value).trim() !== ''),
  )

  const isProjectMapFiltersActive = computed(() =>
    Object.values(projectMapFilters).some((value) => String(value).trim() !== ''),
  )

  async function applyResourceMapFilters(): Promise<void> {
    if (!isResourceMapFiltersActive.value) {
      resourceMapFilteredList.value = null
      return
    }
    resourceMapFilteredList.value = await ResourceService.getCollection(resourceMapFilters, {
      cmnCode: activeCommuneCode.value,
    })
  }

  async function applyProjectMapFilters(): Promise<void> {
    if (!isProjectMapFiltersActive.value) {
      projectMapFilteredList.value = null
      return
    }
    projectMapFilteredList.value = await ProjectService.getCollection(projectMapFilters, {
      cmnCode: activeCommuneCode.value,
    })
  }

  function clearResourceMapFilters(): void {
    Object.assign(resourceMapFilters, createEmptyResourceMapFilters())
    resourceMapFilteredList.value = null
  }

  function clearProjectMapFilters(): void {
    Object.assign(projectMapFilters, createEmptyProjectMapFilters())
    projectMapFilteredList.value = null
  }

  const projectLayer: Ref<Layer | null> = ref(null)
  const projectSubLayers: Ref<Layer[]> = ref([])
  const osmCategoryLayers: Ref<Array<{ mainLayer: Layer; subLayers: Layer[] }>> = ref([])

  const activeItemId: Ref<string | null> = ref(null)
  const activeItem: Ref<Item | null> = ref(null)
  const activeItemType: Ref<ItemType | null> = ref(null)
  const activeItemCoords: Ref<LngLat | null> = ref(null)
  const activeItemLoading: Ref<boolean> = ref(false)
  let activeItemResolveGeneration = 0

  const atlasMaps: Ref<AtlasMap[]> = ref([])
  const atlasSearchText: Ref<string> = ref('')
  const activeAtlas: AtlasActive = reactive({
    leftPanel: {
      active: false,
      atlasID: null,
    },
    rightPanel: {
      active: false,
      atlasID: null,
    },
  })
  const osmCategorySearchText: Ref<string> = ref('')
  const activeOsmCategory: AtlasCategoryActive = reactive({
    rightPanel: {
      active: false,
      categoryId: null,
    },
  })
  let alreadyAddedImageSources: string[] = [] //Used to avoid triggering maplibre event as much time as the layer has been added to the map
  const legendList: Ref<(AppLayerLegendItem | AtlasLayerLegendItem)[]> = ref([])
  const bbox: Ref<LngLatBounds | undefined> = ref(undefined)
  const scaleDenominator = ref(0)
  const tileSize = ref(512)
  const serializedMapState: Ref<string> = ref('')
  const deserializedMapState: Ref<MapState | null> = ref(null)
  const infrastructureFicheModalId: Ref<string | number | null> = ref(null)
  const infrastructureFicheModalAttributeTitle: Ref<string | null> = ref(null)
  const infrastructureFicheModalCompetenceId: Ref<number | null> = ref(null)
  /** Feature properties from map click (popup). Merged with API data to fill attribut values. */
  const infrastructureFicheModalFeatureProperties: Ref<Record<string, unknown> | null> = ref(null)

  const userDroppedLayers: Ref<UserDroppedLayer[]> = ref([])

  /** Draw features and annotations to restore once the map draw API is ready. Set by initMapLayers when deserializing. */
  const pendingDrawRestore: Ref<{
    features: import('geojson').Feature[]
    annotations: Record<string, PointAnnotation>
  } | null> = ref(null)

  /** Zoom level to apply when restoring from a shared link. Set by initMapLayers when deserialized state has zoom. */
  const restoreZoom: Ref<number | null> = ref(null)

  /** User-dropped layers to restore once the map is ready. Set by initMapLayers when deserializing. */
  const pendingUserDroppedRestore: Ref<MapState['userDroppedLayers'] | null> = ref(null)

  /** Draw features temporarily hidden from the map (eye toggle in legend). Key = feature id. */
  const hiddenDrawFeatures: Ref<
    globalThis.Map<string, { feature: import('geojson').Feature; annotation?: PointAnnotation; name: string }>
  > = ref(new globalThis.Map())

  /** Fond (background) layer styles from GET /api/map_styles (fond + indicators only, no OSM). Drives generic layer cards in CARTE section. */
  const fondLayerStyles: Ref<FondLayerStyle[]> = ref([])

  /** OSM entries from GET /api/map_styles (for visible_in filtering in geoportal). */
  const osmEntriesFromMapStyles: Ref<MapStylesListEntry[]> = ref([])

  /** OSM style ids allowed in current view context (always / commune_only / atlas_only). */
  const allowedOsmStyleIds = computed(() => {
    const entries = osmEntriesFromMapStyles.value
    const inCommuneView = communeViewData.value != null
    const inAtlasView =
      activeAtlas.rightPanel.atlasID != null || activeAtlas.leftPanel.atlasID != null
    const context: FondLayerVisibleIn = inCommuneView
      ? 'commune_only'
      : inAtlasView
        ? 'atlas_only'
        : 'always'
    return entries
      .filter((e) => {
        const visibleIn = (e.metadata?.visible_in as FondLayerVisibleIn) ?? 'always'
        return visibleIn === 'always' || visibleIn === context
      })
      .map((e) => e.id)
  })

  /** Style IDs of fond layers that the user has turned on via the card toggle. Layers are loaded only when in this list. */
  const visibleFondStyleIds: Ref<string[]> = ref([])

  /** OSM subcategory style IDs currently visible on the map (e.g. osm_subcategory_173). Toggled via geoportal layer picker. */
  const visibleOsmStyleIds: Ref<string[]> = ref([])

  /**
   * Actual MapLibre style data (paint, layout, iconName) for each OSM style that has
   * been applied to the map. Set by OsmLayerHandler after applyOsmStyleToMap; cleared
   * when the layer is removed. Used by the sidebar to display accurate swatches.
   */
  const osmAppliedStyleData: Ref<
    globalThis.Map<string, { iconName?: string; customIconUrl?: string; paint?: Record<string, unknown>; layout?: Record<string, unknown> }>
  > = ref(new globalThis.Map())

  /**
   * Set of OSM style IDs whose applied map layers need to be removed and
   * re-applied (e.g. after an admin style save). OsmLayerHandler consumes this.
   */
  const pendingOsmStyleRefreshIds: Ref<Set<string>> = ref(new Set())

  function requestOsmStyleRefresh(styleId: string): void {
    const next = new Set(pendingOsmStyleRefreshIds.value)
    next.add(styleId)
    pendingOsmStyleRefreshIds.value = next
  }

  function consumeOsmStyleRefresh(styleId: string): void {
    if (!pendingOsmStyleRefreshIds.value.has(styleId)) return
    const next = new Set(pendingOsmStyleRefreshIds.value)
    next.delete(styleId)
    pendingOsmStyleRefreshIds.value = next
  }

  function setOsmAppliedStyleData(
    styleId: string,
    data: { iconName?: string; customIconUrl?: string; paint?: Record<string, unknown>; layout?: Record<string, unknown> },
  ): void {
    const updated = new globalThis.Map(osmAppliedStyleData.value)
    updated.set(styleId, data)
    osmAppliedStyleData.value = updated
  }

  function clearOsmAppliedStyleData(styleId: string): void {
    if (!osmAppliedStyleData.value.has(styleId)) return
    const updated = new globalThis.Map(osmAppliedStyleData.value)
    updated.delete(styleId)
    osmAppliedStyleData.value = updated
  }

  /** OSM style IDs with OsmClusterService active (point/symbol layers clustered). */
  const osmClusteredStyleIds: Ref<string[]> = ref([])

  /** OSM style IDs for which heatmap mode is enabled (subset of clustered layers). */
  const osmHeatmapStyleIds: Ref<Set<string>> = ref(new Set())

  function addOsmClusteredStyleId(styleId: string): void {
    if (osmClusteredStyleIds.value.includes(styleId)) return
    osmClusteredStyleIds.value = [...osmClusteredStyleIds.value, styleId]
  }

  function removeOsmClusteredStyleId(styleId: string): void {
    osmClusteredStyleIds.value = osmClusteredStyleIds.value.filter((id) => id !== styleId)
  }

  function toggleOsmHeatmap(styleId: string): void {
    if (!styleId) return
    const next = new Set(osmHeatmapStyleIds.value)
    if (next.has(styleId)) next.delete(styleId)
    else next.add(styleId)
    osmHeatmapStyleIds.value = next
  }

  function removeOsmHeatmapStyleId(styleId: string): void {
    if (!osmHeatmapStyleIds.value.has(styleId)) return
    const next = new Set(osmHeatmapStyleIds.value)
    next.delete(styleId)
    osmHeatmapStyleIds.value = next
  }

  function setOsmLayerVisible(styleId: string, visible: boolean): void {
    const alreadyVisible = visibleOsmStyleIds.value.includes(styleId)
    if (alreadyVisible === visible) return
    const set = new Set(visibleOsmStyleIds.value)
    if (visible) set.add(styleId)
    else set.delete(styleId)
    visibleOsmStyleIds.value = [...set]
  }

  /**
   * Selected classification values per OSM styleId (e.g. { key: 'classe', values: ['trunk', 'residential'] }).
   * A styleId absent from the map means "no filter" (whole layer shown). Consumed by OsmLayerHandler,
   * which turns it into a MapLibre `map.setFilter` on the layer's classification property — no server
   * call, since the property is already flattened onto every feature in the vector tile.
   */
  const osmClassificationFilters: Ref<globalThis.Map<string, { key: string; values: string[] }>> = ref(
    new globalThis.Map(),
  )

  function setOsmClassificationFilter(styleId: string, key: string, values: string[]): void {
    const next = new globalThis.Map(osmClassificationFilters.value)
    if (values.length === 0) next.delete(styleId)
    else next.set(styleId, { key, values })
    osmClassificationFilters.value = next
  }

  function clearOsmClassificationFilter(styleId: string): void {
    if (!osmClassificationFilters.value.has(styleId)) return
    const next = new globalThis.Map(osmClassificationFilters.value)
    next.delete(styleId)
    osmClassificationFilters.value = next
  }

  /**
   * OSM/infra style ids whose classification axis (classifications[0].key) was checked against
   * the currently loaded vector tiles and found on NONE of their features for the active commune
   * (e.g. CATEGORIE missing entirely for schools imported outside the Soa data lot — see
   * ISSUE_DATA_CLASSIFICATIONS_TYPE_OUVRAGE.md). Absent from this set means "available or not yet
   * checked" — the sidebar shows the classification checkboxes by default and only hides them once
   * OsmLayerHandler confirms there is nothing to filter on, so a real filter never silently empties
   * the map. Set by OsmLayerHandler.checkClassificationAvailability; cleared whenever the style is
   * removed/refreshed (e.g. on commune change) so it gets re-evaluated against the new tiles.
   */
  const osmClassificationUnavailableStyleIds: Ref<Set<string>> = ref(new Set())

  function setOsmClassificationAvailability(styleId: string, available: boolean): void {
    const isCurrentlyMarkedUnavailable = osmClassificationUnavailableStyleIds.value.has(styleId)
    const shouldBeMarkedUnavailable = !available
    if (isCurrentlyMarkedUnavailable === shouldBeMarkedUnavailable) return
    const next = new Set(osmClassificationUnavailableStyleIds.value)
    if (shouldBeMarkedUnavailable) next.add(styleId)
    else next.delete(styleId)
    osmClassificationUnavailableStyleIds.value = next
  }

  function clearOsmClassificationAvailability(styleId: string): void {
    if (!osmClassificationUnavailableStyleIds.value.has(styleId)) return
    const next = new Set(osmClassificationUnavailableStyleIds.value)
    next.delete(styleId)
    osmClassificationUnavailableStyleIds.value = next
  }

  /**
   * Per-classification-value feature counts, tallied client-side from the same currently loaded
   * vector tiles used by `checkClassificationAvailability` (e.g. { Publique: 43, Privée: 12 } for
   * styleId 'infra_type_ouvrage_27'). There is no backend endpoint for this — markerConfig only
   * carries label/color per value — so it's derived on the map side. Powers the small count badge
   * next to each classification checkbox. A styleId absent (or a value absent within it) means "not
   * computed yet", not "zero" — the badge stays hidden rather than flashing 0.
   */
  const osmClassificationValueCounts: Ref<globalThis.Map<string, globalThis.Map<string, number>>> = ref(
    new globalThis.Map(),
  )

  function setOsmClassificationValueCounts(styleId: string, counts: globalThis.Map<string, number>): void {
    const next = new globalThis.Map(osmClassificationValueCounts.value)
    next.set(styleId, counts)
    osmClassificationValueCounts.value = next
  }

  function clearOsmClassificationValueCounts(styleId: string): void {
    if (!osmClassificationValueCounts.value.has(styleId)) return
    const next = new globalThis.Map(osmClassificationValueCounts.value)
    next.delete(styleId)
    osmClassificationValueCounts.value = next
  }

  /**
   * OSM/infra style ids that failed to load (e.g. 404 because the typeOuvrage was removed
   * from the référentiel). Set by OsmLayerHandler so the sidebar can disable the toggle
   * instead of leaving a stuck "on" eye that renders nothing.
   */
  const osmStyleLoadFailedIds: Ref<Set<string>> = ref(new Set())

  function markOsmStyleLoadFailed(styleId: string): void {
    if (osmStyleLoadFailedIds.value.has(styleId)) return
    const next = new Set(osmStyleLoadFailedIds.value)
    next.add(styleId)
    osmStyleLoadFailedIds.value = next
    setOsmLayerVisible(styleId, false)
  }

  function clearOsmStyleLoadFailed(styleId: string): void {
    if (!osmStyleLoadFailedIds.value.has(styleId)) return
    const next = new Set(osmStyleLoadFailedIds.value)
    next.delete(styleId)
    osmStyleLoadFailedIds.value = next
  }

  function setFondLayerVisible(styleId: string, visible: boolean): void {
    const alreadyVisible = visibleFondStyleIds.value.includes(styleId)
    if (alreadyVisible === visible) return
    const set = new Set(visibleFondStyleIds.value)
    if (visible) set.add(styleId)
    else set.delete(styleId)
    visibleFondStyleIds.value = [...set]
  }

  /** G3: Indicators currently visible on the map (legend source of truth). layerIds used to hide map layers when removing from legend. */
  const visibleIndicatorEntries: Ref<{ id: number; name: string; layerIds?: string[] }[]> = ref([])

  function addVisibleIndicator(entry: {
    id: number
    name: string
    layerIds?: string[]
  }): void {
    if (visibleIndicatorEntries.value.some((e) => e.id === entry.id)) return
    visibleIndicatorEntries.value = [...visibleIndicatorEntries.value, entry]
  }

  function removeVisibleIndicator(atgId: number): void {
    const entry = visibleIndicatorEntries.value.find((e) => e.id === atgId)
    const mapInstance = map.value
    if (entry?.layerIds?.length && mapInstance) {
      for (const layerId of entry.layerIds) {
        if (mapInstance.getLayer(layerId)) {
          mapInstance.setLayoutProperty(layerId, 'visibility', 'none')
        }
      }
    }
    visibleIndicatorEntries.value = visibleIndicatorEntries.value.filter((e) => e.id !== atgId)
  }

  /** G3: Combined list of visible layers (fonds + indicators) for the legend. Fonds first, then indicators. */
  const activeLayersForLegend = computed<ActiveLayerEntry[]>(() => {
    const entries: ActiveLayerEntry[] = []
    for (const styleId of visibleFondStyleIds.value) {
      const fond = fondLayerStyles.value.find((s) => s.id === styleId)
      entries.push({
        id: styleId,
        name: fond?.metadata?.label ?? styleId,
        type: 'fond',
      })
    }
    for (const entry of visibleIndicatorEntries.value) {
      entries.push({
        id: entry.id,
        name: entry.name,
        type: 'indicator',
      })
    }
    return entries
  })

  /** Unified legend list: fonds, indicators, user-dropped, OSM (labels only). Reorderable and deletable. */
  const unifiedMapLegendList: Ref<UnifiedMapLegendEntry[]> = ref([])

  function getUnifiedMapLegendEntryId(entry: UnifiedMapLegendEntry): string {
    return `${entry.type}_${entry.id}`
  }

  const OSM_SUBCATEGORY_PREFIX = 'osm_subcategory_'

  function resolveSubCategoryName(styleId: string): string {
    if (!styleId.startsWith(OSM_SUBCATEGORY_PREFIX)) return styleId
    const subId = Number(styleId.slice(OSM_SUBCATEGORY_PREFIX.length))
    if (Number.isNaN(subId)) return styleId
    const cached = AtlasCategoriesService.getCachedCategories()
    if (!cached) return styleId
    for (const cat of cached) {
      const sub = cat.subCategories?.find((s) => s.id === subId)
      if (sub) return sub.nameFr || sub.nameEn || styleId
    }
    return styleId
  }

  function syncUnifiedMapLegend(): void {
    const mapInstance = map.value
    if (mapInstance) {
      try {
        const style = mapInstance.getStyle()
        const layers = style.layers ?? []
        const indicatorLayerIdsByAtgId = new globalThis.Map<number, string[]>()
        for (const layer of layers) {
          const meta = (layer as { metadata?: { layerGroup?: string } }).metadata
          if (meta?.layerGroup !== 'indicator') continue
          const visibility = mapInstance.getLayoutProperty(layer.id, 'visibility')
          if (visibility === 'none') continue
          const match = layer.id.match(/(?:^indicator-|^infrastructure-indicator-)(\d+)/)
          const atgId = match ? Number(match[1]) : null
          if (atgId == null) continue
          const list = indicatorLayerIdsByAtgId.get(atgId) ?? []
          list.push(layer.id)
          indicatorLayerIdsByAtgId.set(atgId, list)
        }
        const existingIds = new Set(visibleIndicatorEntries.value.map((e) => e.id))
        for (const [atgId, layerIds] of indicatorLayerIdsByAtgId) {
          if (!existingIds.has(atgId)) {
            visibleIndicatorEntries.value = [
              ...visibleIndicatorEntries.value,
              { id: atgId, name: `Indicateur ${atgId}`, layerIds },
            ]
            existingIds.add(atgId)
          }
        }
      } catch {
        // Map not ready or getStyle failed
      }
    }

    const currentEntries: UnifiedMapLegendEntry[] = []
    for (const styleId of visibleFondStyleIds.value) {
      const fond = fondLayerStyles.value.find((s) => s.id === styleId)
      const layerIds = FondLayerService.getLayerIds(styleId)
      let hidden = false
      if (mapInstance && layerIds.length > 0) {
        const firstVisible = layerIds.find((lid) => mapInstance.getLayer(lid))
        const vis = firstVisible
          ? mapInstance.getLayoutProperty(firstVisible, 'visibility')
          : undefined
        hidden = vis === 'none'
      }
      currentEntries.push({
        type: 'fond',
        id: styleId,
        name: fond?.metadata?.label ?? styleId,
        hidden,
      })
    }
    for (const entry of visibleIndicatorEntries.value) {
      currentEntries.push({
        type: 'indicator',
        id: String(entry.id),
        name: entry.name,
        layerIds: entry.layerIds,
      })
    }
    for (const layer of userDroppedLayers.value) {
      if (layer.isShown) {
        currentEntries.push({
          type: 'userDropped',
          id: layer.id,
          name: layer.name,
          layerIds: layer.layerIds,
        })
      }
    }
    const osmIdsInLegend = new Set<string>()
    for (const { mainLayer } of osmCategoryLayers.value) {
      if (mainLayer.isShown) {
        const id = String(mainLayer.id)
        currentEntries.push({ type: 'osm', id, name: mainLayer.name })
        osmIdsInLegend.add(id)
      }
    }
    for (const styleId of visibleOsmStyleIds.value) {
      if (!osmIdsInLegend.has(styleId)) {
        const osmEntry = osmEntriesFromMapStyles.value.find((e) => e.id === styleId)
        const name = osmEntry?.metadata?.label ?? resolveSubCategoryName(styleId)
        currentEntries.push({ type: 'osm', id: styleId, name })
      }
    }
    const drawApi = mapDrawApi.value
    const drawFeaturesList = drawApi?.drawFeatures.value.features ?? []
    const drawAnnotations = drawApi?.pointAnnotations.value ?? {}
    const DRAW_MODE_LABELS: Record<string, string> = {
      point: 'Point',
      linestring: 'Ligne',
      polygon: 'Polygone',
      rectangle: 'Rectangle',
      circle: 'Cercle',
      freehand: 'Main levée',
      sector: 'Secteur',
      'angled-rectangle': 'Rectangle angle',
    }
    for (const f of drawFeaturesList) {
      const feat = f as import('geojson').Feature & { id?: string }
      if ((feat.properties as Record<string, unknown> | null)?.currentlyDrawing) continue
      const id = feat.id != null ? String(feat.id) : ''
      if (!id) continue
      const annotation = drawAnnotations[id]
      const mode = (feat.properties as Record<string, unknown> | null)?.mode as string | undefined
      const name =
        annotation?.title?.trim() ||
        (mode ? DRAW_MODE_LABELS[mode] ?? mode : feat.geometry?.type ?? 'Géométrie')
      currentEntries.push({ type: 'draw', id, name })
    }
    for (const [id, data] of hiddenDrawFeatures.value) {
      if (!currentEntries.some((e) => e.type === 'draw' && e.id === id)) {
        currentEntries.push({ type: 'draw', id, name: data.name, hidden: true })
      }
    }
    const existingIds = new Set(unifiedMapLegendList.value.map(getUnifiedMapLegendEntryId))
    const currentIds = new Set(currentEntries.map((e) => `${e.type}_${e.id}`))
    const kept = unifiedMapLegendList.value.filter((e) => currentIds.has(getUnifiedMapLegendEntryId(e)))
    const newEntries = currentEntries.filter((e) => !existingIds.has(`${e.type}_${e.id}`))
    unifiedMapLegendList.value = [...kept, ...newEntries]
  }

  function removeUnifiedMapLegendEntry(entry: UnifiedMapLegendEntry): void {
    switch (entry.type) {
      case 'fond':
        setFondLayerVisible(entry.id, false)
        break
      case 'indicator':
        removeVisibleIndicator(Number(entry.id))
        break
      case 'userDropped':
        removeUserDroppedLayer(entry.id)
        break
      case 'osm': {
        const group = osmCategoryLayers.value.find((x) => String(x.mainLayer.id) === entry.id)
        if (group) group.mainLayer.isShown = false
        setOsmLayerVisible(entry.id, false)
        break
      }
      case 'draw': {
        hiddenDrawFeatures.value.delete(entry.id)
        mapDrawApi.value?.removeFeatureById(entry.id)
        break
      }
    }
    unifiedMapLegendList.value = unifiedMapLegendList.value.filter(
      (e) => getUnifiedMapLegendEntryId(e) !== getUnifiedMapLegendEntryId(entry),
    )
  }

  function toggleUnifiedMapLegendEntryVisibility(entry: UnifiedMapLegendEntry): void {
    if (entry.type === 'draw') {
      const drawApiRef = mapDrawApi.value
      const willHide = !entry.hidden
      entry.hidden = willHide
      if (willHide) {
        const features = drawApiRef?.drawFeatures.value.features ?? []
        const feat = features.find(
          (f) => String((f as import('geojson').Feature & { id?: string }).id) === entry.id,
        ) as (import('geojson').Feature & { id?: string }) | undefined
        if (feat && drawApiRef) {
          const annotation = drawApiRef.pointAnnotations.value[entry.id]
          const name = entry.name
          hiddenDrawFeatures.value.set(entry.id, {
            feature: { ...feat, properties: { ...feat.properties } },
            annotation,
            name,
          })
          drawApiRef.removeFeatureById(entry.id)
        }
      } else {
        const data = hiddenDrawFeatures.value.get(entry.id)
        if (data && drawApiRef) {
          drawApiRef.restoreFeatures([data.feature], data.annotation ? { [entry.id]: data.annotation } : {})
          hiddenDrawFeatures.value.delete(entry.id)
        }
      }
      return
    }
    const mapInstance = map.value
    const willHide = !entry.hidden
    entry.hidden = willHide
    const visibility = willHide ? 'none' : 'visible'

    if (mapInstance) {
      if (entry.layerIds) {
        for (const layerId of entry.layerIds) {
          if (mapInstance.getLayer(layerId)) {
            mapInstance.setLayoutProperty(layerId, visibility === 'none' ? 'visibility' : 'visibility', visibility)
          }
        }
      }
      const styleLayerIds = getMapLayerIdsForEntry(entry, mapInstance)
      for (const layerId of styleLayerIds) {
        mapInstance.setLayoutProperty(layerId, 'visibility', visibility)
      }
    }
  }

  function getMapLayerIdsForEntry(
    entry: UnifiedMapLegendEntry,
    mapInstance: InstanceType<typeof import('maplibre-gl').Map>,
  ): string[] {
    const allLayers = mapInstance.getStyle()?.layers ?? []
    switch (entry.type) {
      case 'fond': {
        const cachedIds = FondLayerService.getLayerIds(entry.id)
        if (cachedIds.length > 0) return cachedIds
        return allLayers
          .filter((l) => l.id.startsWith(entry.id))
          .map((l) => l.id)
      }
      case 'osm':
        return allLayers
          .filter((l) => l.id === entry.id || l.id.startsWith(`${entry.id}_`) || l.id.startsWith(`${entry.id}-`))
          .map((l) => l.id)
      case 'indicator':
        return entry.layerIds ?? []
      case 'userDropped':
        return entry.layerIds ?? []
      case 'draw':
        return []
      default:
        return []
    }
  }

  /**
   * Reorder map layers to match the unified legend list order (bottom = first entry, top = last entry).
   * Iterates from the end so that the last entry ends up on top.
   */
  function reorderUnifiedMapLayers(): void {
    const mapInstance = map.value
    if (!mapInstance) return
    const list = [...unifiedMapLegendList.value].reverse()
    for (const entry of list) {
      const layerIds = entry.layerIds ?? []
      if (entry.type === 'fond') {
        const fondEntry = FondLayerService.getLayerIds(entry.id)
        for (const id of fondEntry) {
          if (mapInstance.getLayer(id)) {
            try { mapInstance.moveLayer(id) } catch { /* ignore */ }
          }
        }
      }
      for (const id of layerIds) {
        if (mapInstance.getLayer(id)) {
          try { mapInstance.moveLayer(id) } catch { /* ignore */ }
        }
      }
    }
  }

  /** Commune data when fiche is opened from a fond layer click (e.g. couche Communes). Cleared when panel is closed. */
  const communeFicheFromFondLayer: Ref<CommuneData | null> = ref(null)

  /** Commune fiche from vector commune layer click; enriches via API like fond-layer fiche. Cleared when that panel closes. */
  const communeFicheFromCommuneLayer: Ref<CommuneData | null> = ref(null)

  /** Single source for left sidebar Profil + shared fiche: fond layer takes precedence when both are set. */
  const communeFicheForMapProfile = computed(
    () => communeFicheFromFondLayer.value ?? communeFicheFromCommuneLayer.value,
  )

  function setCommuneFicheFromCommuneLayer(communeData: CommuneData | null): void {
    if (communeData === null) {
      communeFicheFromCommuneLayer.value = null
      return
    }
    communeFicheFromCommuneLayer.value = communeData

    const fetchKey = communeData.id ?? communeData.cmn_code ?? null
    if (!fetchKey) {
      console.warn(
        '[myMapStore] setCommuneFicheFromCommuneLayer: no id nor cmn_code, full API fiche not fetched.',
        { id: communeData.id, cmn_code: communeData.cmn_code, cmn_titre: communeData.cmn_titre },
      )
      return
    }

    void (async () => {
      let api =
        communeData.id ?
          await CommuneService.getCommuneById(communeData.id)
        : await CommuneService.getCommuneByCode(communeData.cmn_code!)
      if (!api && communeData.cmn_code && communeData.id) {
        api = await CommuneService.getCommuneByCode(communeData.cmn_code)
      }
      if (!api) return
      const full = mapApiResponseToCommuneData(api)
      if (
        communeFicheFromCommuneLayer.value &&
        (communeFicheFromCommuneLayer.value.id ?? communeFicheFromCommuneLayer.value.cmn_code) ===
          fetchKey
      ) {
        communeFicheFromCommuneLayer.value = full
      }
    })()
  }

  function openCommuneFicheFromFondLayer(communeData: CommuneData): void {
    communeFicheFromFondLayer.value = communeData

    const fetchKey = communeData.id ?? communeData.cmn_code ?? null
    if (!fetchKey) {
      console.warn(
        '[myMapStore] openCommuneFicheFromFondLayer: no id nor cmn_code on commune data, API not called. Properties from tile may use different names.',
        { id: communeData.id, cmn_code: communeData.cmn_code, cmn_titre: communeData.cmn_titre },
      )
      return
    }

    void (async () => {
      let api =
        communeData.id ?
          await CommuneService.getCommuneById(communeData.id)
        : await CommuneService.getCommuneByCode(communeData.cmn_code!)
      if (!api && communeData.cmn_code && communeData.id) {
        api = await CommuneService.getCommuneByCode(communeData.cmn_code)
      }
      if (!api) return
      const full = mapApiResponseToCommuneData(api)
      if (communeFicheFromFondLayer.value && (communeFicheFromFondLayer.value.id ?? communeFicheFromFondLayer.value.cmn_code) === fetchKey) {
        communeFicheFromFondLayer.value = full
      }
    })()
  }

  function closeCommuneFicheFromFondLayer(): void {
    communeFicheFromFondLayer.value = null
  }

  /**
   * When the API returns a polygon boundary, pre-fill communeViewData so the communal mask
   * can render without waiting for GeoJSON or tile queries (same logic as MyMapHeader commune select).
   */
  function applyCommuneViewFromApiGeometry(commune: CommuneApiResponse): void {
    // Set the cmnCode as soon as it's known, independent of geometry: the collection/search
    // endpoint this is usually fed from doesn't return geometry, only the detail endpoint does.
    if (commune.cmnCode) {
      lockedCommuneCode.value = commune.cmnCode
    }
    const geometry = getCommuneGeometryFromApi(commune)
    const name = commune.cmnTitre?.trim()
    if (!name || !geometry) return
    void import('@turf/turf').then(({ bbox: turfBbox }) => {
      void import('maplibre-gl').then(({ LngLatBounds }) => {
        const [minLng, minLat, maxLng, maxLat] = turfBbox(geometry)
        const bounds = new LngLatBounds([minLng, minLat], [maxLng, maxLat])
        communeViewData.value = { name, cmnCode: commune.cmnCode, bounds, geometry }
      })
    })
  }

  /**
   * Zooms directly to commune bounds using MapLibre feature query (querySourceFeatures).
   * Does NOT touch mapSearch/communeViewData to avoid URL changes and applyCommuneView side effects.
   * Uses the local GeoJSON as primary bounds source (instant, no tile dependency).
   */
  async function zoomToCommuneBounds(
    communeName: string,
    centroidLng?: number,
    centroidLat?: number,
  ): Promise<void> {
    const mapInstance = myMap.value?.map
    if (!mapInstance) return

    // Priority 1: local GeoJSON lookup — instant, no tile/visibility dependency.
    const geoJsonData = communeName ? await getCommuneViewDataFromGeoJSON(communeName) : null
    if (geoJsonData) {
      const sw = geoJsonData.bounds.getSouthWest()
      const ne = geoJsonData.bounds.getNorthEast()
      mapInstance.fitBounds(
        [[sw.lng, sw.lat], [ne.lng, ne.lat]],
        { padding: 40, duration: 800 },
      )
      return
    }

    // Priority 2: centroid flyTo — no tile query needed.
    if (
      typeof centroidLng === 'number' &&
      typeof centroidLat === 'number' &&
      Number.isFinite(centroidLng) &&
      Number.isFinite(centroidLat)
    ) {
      mapInstance.flyTo({ center: [centroidLng, centroidLat], zoom: 10, speed: 1 })
      return
    }

    // Priority 3: last resort — tile query (only works when commune layer is visible).
    const data = await getCommuneBoundsFromMap(mapInstance, communeName)
    if (data) {
      const sw = data.bounds.getSouthWest()
      const ne = data.bounds.getNorthEast()
      mapInstance.fitBounds(
        [[sw.lng, sw.lat], [ne.lng, ne.lat]],
        { padding: 40, duration: 800 },
      )
    }
  }

  /** Selects a commune feature by its cmn_code for visual highlight on the map. */
  function selectCommuneFeatureByCode(cmn_code: string): void {
    const mapInstance = myMap.value?.map
    if (!mapInstance || !cmn_code) return
    try {
      CommuneLayerService.selectFeature(mapInstance, cmn_code)
    } catch { /* ignore */ }
  }

  /** Clears all commune fiche data, deselects the commune on the map and resets view to Cameroon bounds. */
  function clearCommuneFicheAndDeselect(): void {
    communeFicheFromFondLayer.value = null
    communeFicheFromCommuneLayer.value = null
    communeViewData.value = null
    lockedCommuneCode.value = null
    mapSearch.value = null
    const mapInstance = myMap.value?.map
    if (mapInstance) {
      CommuneLayerService.deselectAllFeatures(mapInstance)
    }
    myMap.value?.fitToDefaultBounds?.()
  }

  let _fondFetchInFlight: Promise<void> | null = null

  async function fetchFondLayerStyles(): Promise<void> {
    if (_fondFetchInFlight) return _fondFetchInFlight
    _fondFetchInFlight = (async () => {
      try {
        const { fondAndIndicators, osmEntries } = await getMapStylesListSplit()
        fondLayerStyles.value = fondAndIndicators as FondLayerStyle[]
        osmEntriesFromMapStyles.value = osmEntries
      } finally {
        _fondFetchInFlight = null
      }
    })()
    return _fondFetchInFlight
  }

  function addUserDroppedLayer(name: string, geojson: GeoJSON.GeoJSON): UserDroppedLayer | null {
    const mapInstance = map.value ?? undefined
    const id = crypto.randomUUID()
    const result = MapService.addUserGeoJsonLayer(mapInstance, geojson, id)
    if (!result) return null
    const layer: UserDroppedLayer = {
      id,
      name,
      sourceId: result.sourceId,
      layerIds: result.layerIds,
      hitLayerIds: result.hitLayerIds,
      geojson,
      isShown: true,
    }
    userDroppedLayers.value = [...userDroppedLayers.value, layer]
    return layer
  }

  function removeUserDroppedLayer(layerId: string) {
    const layer = userDroppedLayers.value.find((l) => l.id === layerId)
    if (!layer) return
    MapService.removeUserGeoJsonLayer(map.value ?? undefined, layer.sourceId, layer.layerIds)
    userDroppedLayers.value = userDroppedLayers.value.filter((l) => l.id !== layerId)
  }

  function setUserDroppedLayerVisibility(layerId: string, visible: boolean) {
    const layer = userDroppedLayers.value.find((l) => l.id === layerId)
    if (!layer) return
    layer.isShown = visible
    MapService.setUserGeoJsonLayerVisibility(map.value ?? undefined, layer.layerIds, visible)
  }

  function zoomToUserDroppedLayer(layerId: string): void {
    const mapInstance = map.value
    if (!mapInstance) return
    const layer = userDroppedLayers.value.find((l) => l.id === layerId)
    if (!layer) return
    import('@turf/turf').then(({ bbox }) => {
      try {
        const [minLng, minLat, maxLng, maxLat] = bbox(layer.geojson as Parameters<typeof bbox>[0])
        if ([minLng, minLat, maxLng, maxLat].some((v) => !isFinite(v))) return
        mapInstance.fitBounds([[minLng, minLat], [maxLng, maxLat]], { padding: 60, duration: 800 })
      } catch {
        // Invalid geometry — do nothing
      }
    })
  }

  function applyPendingUserDroppedRestore(): void {
    const pending = pendingUserDroppedRestore.value
    if (!pending?.length) return
    for (const item of pending) {
      const added = addUserDroppedLayer(item.name, item.geojson)
      if (added && !item.isShown) setUserDroppedLayerVisibility(added.id, false)
    }
    pendingUserDroppedRestore.value = null
  }

  function openInfrastructureFicheModal(
    id: string | number,
    attributeTitle?: string,
    competenceId?: number,
    featureProperties?: Record<string, unknown> | null,
  ) {
    infrastructureFicheModalId.value = id
    infrastructureFicheModalAttributeTitle.value = attributeTitle ?? null
    infrastructureFicheModalCompetenceId.value = competenceId ?? null
    infrastructureFicheModalFeatureProperties.value = featureProperties ?? null
  }

  function closeInfrastructureFicheModal() {
    infrastructureFicheModalId.value = null
    infrastructureFicheModalAttributeTitle.value = null
    infrastructureFicheModalCompetenceId.value = null
    infrastructureFicheModalFeatureProperties.value = null
  }

  async function initMapLayers() {
    // Capture draw restore data synchronously before any async work to avoid race
    // conditions with onMapReady (which may fire before the await below completes).
    if (deserializedMapState.value?.drawFeatures?.length) {
      pendingDrawRestore.value = {
        features: deserializedMapState.value.drawFeatures,
        annotations: deserializedMapState.value.drawPointAnnotations ?? {},
      }
    }

    await AppLayersService.initApplicationLayers()
    // TODO: Réactiver lorsque l'endpoint /api/atlases sera disponible
    // await AtlasMapService.initAtlasLayers(useMyMapStore(), useAtlasStore())
    if (deserializedMapState.value) {
      const stateFondIds = deserializedMapState.value.visibleFondStyleIds
      if (stateFondIds?.length) {
        console.info('[mapState] Restoring fond layers from link:', stateFondIds)
        await fetchFondLayerStyles()
        console.info('[mapState] After fetchFondLayerStyles: fondLayerStyles.length=', fondLayerStyles.value.length)
      }
      bbox.value = deserializedMapState.value.bbox
      if (typeof deserializedMapState.value.zoom === 'number') {
        restoreZoom.value = deserializedMapState.value.zoom
      }
      const bounds: [number, number, number, number] = [
        bbox.value._sw.lng,
        bbox.value._sw.lat,
        bbox.value._ne.lng,
        bbox.value._ne.lat,
      ]
      myMap.value?.map?.fitBounds(bounds)
      for (const thematicMap of atlasMaps.value) {
        if (thematicMap.mainLayer.isShown) {
          updateAtlasLayersVisibility(thematicMap.id)
        }
      }
      legendList.value.sort((a, b) => {
        const indexA = deserializedMapState.value!.order.indexOf(a.id)
        const indexB = deserializedMapState.value!.order.indexOf(b.id)
        return indexA - indexB
      })
      updateMapLayersOrder()
      if (deserializedMapState.value.visibleFondStyleIds?.length) {
        visibleFondStyleIds.value = [...deserializedMapState.value.visibleFondStyleIds]
        console.info('[mapState] visibleFondStyleIds set to', visibleFondStyleIds.value)
      }
      if (deserializedMapState.value.visibleOsmStyleIds?.length) {
        visibleOsmStyleIds.value = [...deserializedMapState.value.visibleOsmStyleIds]
        console.info('[mapState] visibleOsmStyleIds set to', visibleOsmStyleIds.value)
      }
      pendingUserDroppedRestore.value =
      deserializedMapState.value.userDroppedLayers?.length
        ? deserializedMapState.value.userDroppedLayers
        : null
    }
    deserializedMapState.value = null
    syncUnifiedMapLegend()
    applicationStore.isLoading = false
  }

  // Activate a watcher for app layers status
  LegendService.watchAppLayersVisibilityChanges(
    actorLayer,
    projectLayer,
    resourceLayer,
    legendList,
    atlasMaps,
  )

  function getSerializedMapState(): string {
    return MapStoreSerializationService.serializeStore(useMyMapStore())
  }

  function deserializeMapState() {
    deserializedMapState.value = MapStoreSerializationService.deserializeStore(
      serializedMapState.value,
    )
    serializedMapState.value = ''
  }

  function updateAtlasLayersVisibility(atlasMapId: string, updateLegend = true) {
    AtlasMapService.handleAtlasLayersVisibility(atlasMaps.value, myMap.value?.map, atlasMapId)
    alreadyAddedImageSources = [...new Set([...alreadyAddedImageSources, atlasMapId])]
    if (updateLegend) {
      LegendService.updateLegendList(atlasMapId, LayerType.ATLAS_LAYER, legendList, atlasMaps)
    }
  }

  function updateMapLayersOrder() {
    LegendService.updateLayersOrder(myMap.value?.map as maplibregl.Map, legendList)
  }

  function updateAtlasSubLayersOrder(atlasMapLayer: AtlasLayerLegendItem) {
    LegendService.updateAtlasSubLayersOrder(atlasMapLayer, atlasMaps)
    updateAtlasLayersVisibility(atlasMapLayer.id, false)
  }

  // When a user quit the map and come back, we wait for all the sources/layers to be loaded then we
  // reorder the layers according to the legend
  function setMapLayersOrderOnMapReMount() {
    const map = myMap.value?.map as maplibregl.Map
    if (!map) return
    const sources = map.getStyle().sources
    const numberOfSources = Object.keys(sources).length - 1 //We don't want basemap provider's (Maptiler/Mapbox) source
    const numberOfLegendItems = legendList.value.length
    if (numberOfSources === numberOfLegendItems) {
      if (map.loaded()) {
        updateMapLayersOrder()
      } else {
        map.on('idle', () => {
          // Avoid an infinite loop as layers ordering triggers 'idle' event
          if (isLayersReorderingAlreadyTriggering.value) return
          updateMapLayersOrder()
          isLayersReorderingAlreadyTriggering.value = true
        })
      }
    }
  }

  watch(
    [
      () => visibleFondStyleIds.value,
      () => visibleIndicatorEntries.value,
      () => userDroppedLayers.value,
      () => osmCategoryLayers.value,
      () => visibleOsmStyleIds.value,
      () => mapDrawApi.value?.drawFeatures.value.features.length ?? 0,
      () => mapDrawApi.value?.pointAnnotations.value,
    ],
    () => syncUnifiedMapLegend(),
    { deep: true },
  )

  // PopUp Management: load project or resource by id only (GET /api/projects/{id} or /api/resources/{id}).
  watch(
    () => activeItemId.value,
    async () => {
      const generation = ++activeItemResolveGeneration
      activeItem.value = null
      activeItemType.value = null

      if (activeItemId.value == null) {
        activeItemLoading.value = false
        return
      }

      const id = String(activeItemId.value).trim()
      if (id === '') {
        activeItemLoading.value = false
        return
      }

      console.log('[myMapStore] CLICK → resolve item by id', { id })
      activeItemLoading.value = true
      try {
        try {
          activeItem.value = await ProjectService.get({ id })
          activeItemType.value = ItemType.PROJECT
          return
        } catch {
          /* not a project or request failed */
        }

        try {
          const resource = await ResourceService.get({ id })
          if (resource != null) {
            activeItem.value = resource
            activeItemType.value = ItemType.RESOURCE
            return
          }
        } catch {
          /* not a resource or request failed */
        }
      } catch (err) {
        console.error('[myMapStore] Failed to resolve active map item', err)
      } finally {
        if (generation === activeItemResolveGeneration) {
          activeItemLoading.value = false
        }
      }
    },
  )

  return {
    isRightSidebarShown,
    isLeftSidebarShown,
    rightSidebarPanelUnfolded,
    isMapAlreadyBeenMounted,
    isLayersReorderingAlreadyTriggering,
    isMapExportActive,
    myMap,
    map,
    mapCanvasToDataUrl,
    mapSearch,
    lockedCommuneName,
    setLockedCommuneName,
    communeViewData,
    lockedCommuneCode,
    activeCommuneCode,
    actorLayer,
    actorSubLayers,
    projectLayer,
    projectSubLayers,
    resourceLayer,
    resourceSubLayers,
    resourceMapFilters,
    resourceMapFilteredList,
    isResourceMapFiltersActive,
    applyResourceMapFilters,
    clearResourceMapFilters,
    projectMapFilters,
    projectMapFilteredList,
    isProjectMapFiltersActive,
    applyProjectMapFilters,
    clearProjectMapFilters,
    osmCategoryLayers,
    atlasMaps,
    atlasSearchText,
    activeAtlas,
    osmCategorySearchText,
    activeOsmCategory,
    initMapLayers,
    updateAtlasLayersVisibility,
    legendList,
    updateMapLayersOrder,
    updateAtlasSubLayersOrder,
    setMapLayersOrderOnMapReMount,
    bbox,
    scaleDenominator,
    tileSize,
    getSerializedMapState,
    serializedMapState,
    deserializedMapState,
    deserializeMapState,
    activeItemId,
    activeItemType,
    activeItem,
    activeItemCoords,
    activeItemLoading,
    infrastructureFicheModalId,
    infrastructureFicheModalAttributeTitle,
    infrastructureFicheModalCompetenceId,
    infrastructureFicheModalFeatureProperties,
    openInfrastructureFicheModal,
    closeInfrastructureFicheModal,
    userDroppedLayers,
    addUserDroppedLayer,
    removeUserDroppedLayer,
    setUserDroppedLayerVisibility,
    zoomToUserDroppedLayer,
    applyPendingUserDroppedRestore,
    fondLayerStyles,
    fetchFondLayerStyles,
    osmEntriesFromMapStyles,
    allowedOsmStyleIds,
    visibleFondStyleIds,
    setFondLayerVisible,
    visibleOsmStyleIds,
    setOsmLayerVisible,
    osmClassificationFilters,
    setOsmClassificationFilter,
    clearOsmClassificationFilter,
    osmClassificationUnavailableStyleIds,
    setOsmClassificationAvailability,
    clearOsmClassificationAvailability,
    osmClassificationValueCounts,
    setOsmClassificationValueCounts,
    clearOsmClassificationValueCounts,
    osmStyleLoadFailedIds,
    markOsmStyleLoadFailed,
    clearOsmStyleLoadFailed,
    osmAppliedStyleData,
    setOsmAppliedStyleData,
    clearOsmAppliedStyleData,
    pendingOsmStyleRefreshIds,
    requestOsmStyleRefresh,
    consumeOsmStyleRefresh,
    osmClusteredStyleIds,
    osmHeatmapStyleIds,
    addOsmClusteredStyleId,
    removeOsmClusteredStyleId,
    toggleOsmHeatmap,
    removeOsmHeatmapStyleId,
    visibleIndicatorEntries,
    addVisibleIndicator,
    removeVisibleIndicator,
    activeLayersForLegend,
    unifiedMapLegendList,
    syncUnifiedMapLegend,
    removeUnifiedMapLegendEntry,
    toggleUnifiedMapLegendEntryVisibility,
    reorderUnifiedMapLayers,
    communeFicheFromFondLayer,
    communeFicheFromCommuneLayer,
    communeFicheForMapProfile,
    setCommuneFicheFromCommuneLayer,
    openCommuneFicheFromFondLayer,
    closeCommuneFicheFromFondLayer,
    selectCommuneFeatureByCode,
    clearCommuneFicheAndDeselect,
    applyCommuneViewFromApiGeometry,
    zoomToCommuneBounds,
    rightSidebarPanelVariant,
    mapDrawApi,
    setMapDrawApi,
    pendingDrawRestore,
    pendingUserDroppedRestore,
    restoreZoom,
  }
})
